import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  I18nManager,
  Modal,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { changeLanguage } from '../config/i18n';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  authenticateWithBiometrics,
  checkBiometricAvailability,
  checkBiometricEnabled,
  disableBiometric,
  saveBiometricCredentials,
} from '../utils/biometric';
import logger from '../utils/logger';

export const SettingsScreen = ({ navigation }: any) => {
  const { t, i18n } = useTranslation();
  const { theme, themeMode, setThemeMode, isDark } = useTheme();
  const { logout, user } = useAuth();
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState<string>('');

  // Modal state for password prompt (cross-platform solution)
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;

  useEffect(() => {
    navigation.setOptions({ title: t('settings.title') });
  }, [navigation, t, i18n.language]);

  useEffect(() => {
    checkBiometricStatus();
  }, []);

  const checkBiometricStatus = async () => {
    const { enabled, type } = await checkBiometricAvailability();
    setBiometricAvailable(enabled);
    setBiometricType(type);

    if (enabled) {
      const isEnabled = await checkBiometricEnabled();
      setBiometricEnabled(isEnabled);
    }
  };

  const handleBiometricToggle = async (value: boolean) => {
    if (value) {
      // Enable biometric - authenticate first
      try {
        const authenticated = await authenticateWithBiometrics();
        if (authenticated) {
          // Use cross-platform modal instead of Alert.prompt (iOS only)
          setPasswordInput('');
          setShowPasswordModal(true);
        }
      } catch (error) {
        logger.error('Failed to enable biometric:', error);
        Alert.alert('Error', 'Failed to enable biometric authentication');
      }
    } else {
      // Disable biometric
      Alert.alert(
        'Disable Biometric Login',
        'Are you sure you want to disable biometric authentication?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Disable',
            style: 'destructive',
            onPress: async () => {
              await disableBiometric();
              setBiometricEnabled(false);
              Alert.alert('Success', 'Biometric authentication disabled');
            },
          },
        ],
      );
    }
  };

  // Handle password submission from modal
  const handlePasswordSubmit = async () => {
    if (!passwordInput.trim()) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء إدخال كلمة المرور' : 'Please enter your password',
      );
      return;
    }
    if (user?.email) {
      // Security fix: Only save email flag, not password
      await saveBiometricCredentials(user.email);
      setBiometricEnabled(true);
      setShowPasswordModal(false);
      setPasswordInput('');
      Alert.alert(
        isArabic ? 'نجح' : 'Success',
        isArabic
          ? 'تم تفعيل المصادقة البيومترية. ستحتاج إلى إدخال كلمة المرور في المرة القادمة.'
          : 'Biometric enabled. You will need to enter your password on next login.',
      );
    } else {
      Alert.alert(isArabic ? 'خطأ' : 'Error', isArabic ? 'المستخدم غير موجود' : 'User not found');
    }
  };

  const handleLanguageChange = async (lang: string) => {
    const currentLang = i18n.language;
    if (lang === currentLang) return;

    const isRTL = lang === 'ar';
    const needsReload = I18nManager.isRTL !== isRTL;

    if (needsReload) {
      // Show confirmation for RTL change that requires reload
      Alert.alert(
        t('settings.language'),
        lang === 'ar'
          ? 'سيتم إعادة تشغيل التطبيق لتطبيق التغييرات'
          : 'App will restart to apply changes',
        [
          { text: t('common.cancel'), style: 'cancel' },
          {
            text: t('common.confirm'),
            onPress: async () => {
              try {
                await changeLanguage(lang);
              } catch (error) {
                Alert.alert(t('common.error'), t('errors.unknownError'));
              }
            },
          },
        ],
      );
    } else {
      // No reload needed, just change language
      try {
        await changeLanguage(lang);
      } catch (error) {
        Alert.alert(t('common.error'), t('errors.unknownError'));
      }
    }
  };

  const handleThemeChange = async (mode: 'light' | 'dark' | 'system') => {
    await setThemeMode(mode);
  };

  const handleLogout = () => {
    Alert.alert(t('common.logout'), t('common.confirm') + '?', [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.logout'),
        style: 'destructive',
        onPress: logout,
      },
    ]);
  };

  const renderSection = (title: string, children: React.ReactNode) => (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: theme.colors.textSecondary }]}>{title}</Text>
      <View
        style={[
          styles.sectionContent,
          { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
        ]}
      >
        {children}
      </View>
    </View>
  );

  const renderSettingRow = (
    label: string,
    value?: string,
    onPress?: () => void,
    rightElement?: React.ReactNode,
    isLast?: boolean,
    accessibilityLabel?: string,
    accessibilityHint?: string,
  ) => (
    <TouchableOpacity
      style={[
        styles.settingRow,
        { flexDirection: isRTL ? 'row-reverse' : 'row' },
        !isLast && { borderBottomWidth: 1, borderBottomColor: theme.colors.border },
      ]}
      onPress={onPress}
      disabled={!onPress && !rightElement}
      accessible={true}
      accessibilityLabel={accessibilityLabel || `${label}${value ? `: ${value}` : ''}`}
      accessibilityHint={
        accessibilityHint ||
        (onPress ? (isArabic ? 'انقر نقرًا مزدوجًا للفتح' : 'Double tap to open') : undefined)
      }
      accessibilityRole={onPress ? 'button' : 'text'}
    >
      <View style={styles.settingLeft}>
        <Text style={[styles.settingLabel, { color: theme.colors.text }]}>{label}</Text>
        {value && (
          <Text style={[styles.settingValue, { color: theme.colors.textSecondary }]}>{value}</Text>
        )}
      </View>
      {rightElement && <View style={styles.settingRight}>{rightElement}</View>}
    </TouchableOpacity>
  );

  return (
    <>
      <ScrollView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
        showsVerticalScrollIndicator={false}
      >
        {/* User Info */}
        <View style={[styles.userCard, { backgroundColor: theme.colors.card }]}>
          <View style={[styles.avatar, { backgroundColor: theme.colors.primary }]}>
            <Text style={styles.avatarText}>{user?.email?.charAt(0).toUpperCase() || 'U'}</Text>
          </View>
          <Text style={[styles.userName, { color: theme.colors.text }]}>
            {user?.email?.split('@')[0] || 'User'}
          </Text>
          <Text style={[styles.userEmail, { color: theme.colors.textSecondary }]}>
            {user?.email || 'user@example.com'}
          </Text>
          {user?.organization?.name && (
            <View style={styles.organizationBadge}>
              <Ionicons name="business" size={16} color={theme.colors.primary} />
              <Text style={[styles.organizationText, { color: theme.colors.primary }]}>
                {user.organization.name}
              </Text>
            </View>
          )}
        </View>

        {/* General Settings */}
        {renderSection(
          t('settings.general'),
          <>
            {renderSettingRow(
              t('settings.language'),
              i18n.language === 'ar' ? 'العربية' : 'English',
              () => {
                Alert.alert(t('settings.language'), '', [
                  {
                    text: 'العربية',
                    onPress: () => handleLanguageChange('ar'),
                  },
                  {
                    text: 'English',
                    onPress: () => handleLanguageChange('en'),
                  },
                  { text: t('common.cancel'), style: 'cancel' },
                ]);
              },
              undefined,
              undefined,
              isArabic
                ? `اللغة: ${i18n.language === 'ar' ? 'العربية' : 'English'}`
                : `Language: ${i18n.language === 'ar' ? 'Arabic' : 'English'}`,
              isArabic
                ? 'انقر نقرًا مزدوجًا لتغيير لغة التطبيق'
                : 'Double tap to change app language',
            )}
            {renderSettingRow(
              t('settings.theme'),
              themeMode === 'light'
                ? t('settings.light')
                : themeMode === 'dark'
                ? t('settings.dark')
                : t('settings.system'),
              () => {
                Alert.alert(t('settings.theme'), '', [
                  {
                    text: t('settings.light'),
                    onPress: () => handleThemeChange('light'),
                  },
                  {
                    text: t('settings.dark'),
                    onPress: () => handleThemeChange('dark'),
                  },
                  {
                    text: t('settings.system'),
                    onPress: () => handleThemeChange('system'),
                  },
                  { text: t('common.cancel'), style: 'cancel' },
                ]);
              },
              undefined,
              true,
              isArabic
                ? `المظهر: ${
                    themeMode === 'light' ? 'فاتح' : themeMode === 'dark' ? 'داكن' : 'النظام'
                  }`
                : `Theme: ${
                    themeMode === 'light' ? 'Light' : themeMode === 'dark' ? 'Dark' : 'System'
                  }`,
              isArabic
                ? 'انقر نقرًا مزدوجًا لتغيير مظهر التطبيق'
                : 'Double tap to change app theme',
            )}
          </>,
        )}

        {/* Notifications */}
        {renderSection(
          t('settings.notifications'),
          <>
            {renderSettingRow(
              t('settings.notificationSettings'),
              i18n.language === 'ar'
                ? 'إدارة جميع إعدادات الإشعارات'
                : 'Manage all notification settings',
              () => {
                if (navigation) {
                  navigation.navigate('NotificationSettings');
                } else {
                  Alert.alert(
                    t('settings.notificationSettings'),
                    t('settings.manageNotifications'),
                  );
                }
              },
              <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />,
              true,
              isArabic
                ? 'إعدادات الإشعارات: إدارة جميع إعدادات الإشعارات'
                : 'Notification Settings: Manage all notification settings',
              isArabic
                ? 'انقر نقرًا مزدوجًا لفتح شاشة إعدادات الإشعارات'
                : 'Double tap to open notification settings screen',
            )}
          </>,
        )}

        {/* Security */}
        {biometricAvailable &&
          renderSection(
            i18n.language === 'ar' ? 'الأمان' : 'Security',
            <>
              {renderSettingRow(
                `${biometricType} Login`,
                biometricEnabled
                  ? i18n.language === 'ar'
                    ? 'مفعل'
                    : 'Enabled'
                  : i18n.language === 'ar'
                  ? 'معطل'
                  : 'Disabled',
                undefined,
                <Switch
                  value={biometricEnabled}
                  onValueChange={handleBiometricToggle}
                  trackColor={{ false: theme.colors.disabled, true: theme.colors.primary + '80' }}
                  thumbColor={biometricEnabled ? theme.colors.primary : theme.colors.placeholder}
                  accessible={true}
                  accessibilityLabel={
                    isArabic
                      ? `تسجيل الدخول البيومتري ${biometricType}: ${
                          biometricEnabled ? 'مفعل' : 'معطل'
                        }`
                      : `${biometricType} Login: ${biometricEnabled ? 'Enabled' : 'Disabled'}`
                  }
                  accessibilityHint={
                    isArabic
                      ? biometricEnabled
                        ? 'انقر نقرًا مزدوجًا لتعطيل تسجيل الدخول البيومتري'
                        : 'انقر نقرًا مزدوجًا لتفعيل تسجيل الدخول البيومتري'
                      : biometricEnabled
                      ? 'Double tap to disable biometric login'
                      : 'Double tap to enable biometric login'
                  }
                  accessibilityRole="switch"
                />,
                true,
                isArabic
                  ? `${biometricType} تسجيل الدخول: ${biometricEnabled ? 'مفعل' : 'معطل'}`
                  : `${biometricType} Login: ${biometricEnabled ? 'Enabled' : 'Disabled'}`,
                undefined,
              )}
            </>,
          )}

        {/* Organization Info */}
        {user?.organization &&
          renderSection(
            i18n.language === 'ar' ? 'معلومات المنظمة' : 'Organization Info',
            <>
              {renderSettingRow(
                i18n.language === 'ar' ? 'اسم المنظمة' : 'Organization Name',
                user.organization.name,
              )}
              {user.roles &&
                user.roles.length > 0 &&
                renderSettingRow(
                  i18n.language === 'ar' ? 'الدور' : 'Role',
                  (() => {
                    const firstRole = user.roles[0];
                    return typeof firstRole === 'string'
                      ? firstRole
                      : i18n.language === 'ar'
                      ? firstRole.nameAr
                      : firstRole.name;
                  })(),
                  undefined,
                  true,
                )}
            </>,
          )}

        {/* Security & 2FA */}
        {renderSection(
          isArabic ? 'الأمان والمصادقة' : 'Security & Authentication',
          <>
            {renderSettingRow(
              isArabic ? 'المصادقة الثنائية' : 'Two-Factor Authentication',
              undefined,
              () => {
                if (navigation) {
                  navigation.navigate('TwoFactorSettings');
                }
              },
              <Ionicons name="shield-checkmark" size={20} color={theme.colors.primary} />,
              undefined,
              isArabic
                ? 'المصادقة الثنائية: إدارة إعدادات الأمان'
                : 'Two-Factor Authentication: Manage security settings',
              isArabic
                ? 'انقر نقرًا مزدوجًا لفتح إعدادات المصادقة الثنائية'
                : 'Double tap to open two-factor authentication settings',
            )}
            {renderSettingRow(
              isArabic ? 'تغيير كلمة المرور' : 'Change Password',
              undefined,
              () => {
                if (navigation) {
                  navigation.navigate('Profile');
                }
              },
              <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />,
              true,
              isArabic ? 'تغيير كلمة المرور' : 'Change Password',
              isArabic
                ? 'انقر نقرًا مزدوجًا للانتقال إلى الملف الشخصي لتغيير كلمة المرور'
                : 'Double tap to go to profile to change password',
            )}
          </>,
        )}

        {/* Account */}
        {renderSection(
          t('settings.account'),
          <>
            {renderSettingRow(
              t('profile.title'),
              undefined,
              () => {
                if (navigation) {
                  navigation.navigate('Profile');
                } else {
                  Alert.alert(
                    t('profile.title'),
                    i18n.language === 'ar' ? 'صفحة الملف الشخصي' : 'Profile screen',
                  );
                }
              },
              undefined,
              true,
              isArabic
                ? 'الملف الشخصي: عرض وتعديل معلوماتك الشخصية'
                : 'Profile: View and edit your personal information',
              isArabic ? 'انقر نقرًا مزدوجًا لفتح الملف الشخصي' : 'Double tap to open profile',
            )}
          </>,
        )}

        {/* About */}
        {renderSection(
          t('settings.about'),
          <>
            {renderSettingRow(t('settings.version'), Constants.expoConfig?.version ?? '2.3.32')}
            {renderSettingRow(t('settings.privacy'), undefined, () => {
              Alert.alert(t('settings.privacy'), 'Privacy policy details...');
            })}
            {renderSettingRow(t('settings.terms'), undefined, () => {
              Alert.alert(t('settings.terms'), 'Terms and conditions details...');
            })}
            {renderSettingRow(
              t('settings.support'),
              undefined,
              () => {
                Alert.alert(t('settings.support'), 'support@iotedge.com');
              },
              undefined,
              true,
            )}
          </>,
        )}

        {/* Logout Button */}
        <TouchableOpacity
          style={[styles.logoutButton, { backgroundColor: theme.colors.error }]}
          onPress={handleLogout}
          accessible={true}
          accessibilityLabel={isArabic ? 'تسجيل الخروج من التطبيق' : 'Logout from app'}
          accessibilityHint={
            isArabic
              ? 'انقر نقرًا مزدوجًا لتسجيل الخروج من حسابك'
              : 'Double tap to logout from your account'
          }
          accessibilityRole="button"
        >
          <Text style={styles.logoutButtonText}>{t('common.logout')}</Text>
        </TouchableOpacity>

        <View style={styles.footer}>
          <Text style={[styles.footerText, { color: theme.colors.textSecondary }]}>
            IoT Edge © {new Date().getFullYear()}
          </Text>
        </View>
      </ScrollView>

      {/* Cross-platform Password Modal (replaces iOS-only Alert.prompt) */}
      <Modal
        visible={showPasswordModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowPasswordModal(false);
          setPasswordInput('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
              Enable Biometric Login
            </Text>
            <Text style={[styles.modalSubtitle, { color: theme.colors.textSecondary }]}>
              Please enter your password to enable biometric authentication
            </Text>
            <TextInput
              style={[
                styles.modalInput,
                {
                  color: theme.colors.text,
                  borderColor: theme.colors.border,
                  backgroundColor: theme.colors.background,
                },
              ]}
              secureTextEntry
              placeholder="Enter password"
              placeholderTextColor={theme.colors.textSecondary}
              value={passwordInput}
              onChangeText={setPasswordInput}
              autoFocus
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonCancel]}
                onPress={() => {
                  setShowPasswordModal(false);
                  setPasswordInput('');
                }}
                accessible={true}
                accessibilityLabel={isArabic ? 'إلغاء' : 'Cancel'}
                accessibilityHint={
                  isArabic
                    ? 'انقر نقرًا مزدوجًا لإلغاء تفعيل المصادقة البيومترية'
                    : 'Double tap to cancel biometric authentication setup'
                }
                accessibilityRole="button"
              >
                <Text style={[styles.modalButtonText, { color: theme.colors.textSecondary }]}>
                  Cancel
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.modalButtonSubmit,
                  { backgroundColor: theme.colors.primary },
                ]}
                onPress={handlePasswordSubmit}
                accessible={true}
                accessibilityLabel={
                  isArabic ? 'تفعيل المصادقة البيومترية' : 'Enable biometric authentication'
                }
                accessibilityHint={
                  isArabic
                    ? 'انقر نقرًا مزدوجًا لتفعيل تسجيل الدخول البيومتري'
                    : 'Double tap to enable biometric login'
                }
                accessibilityRole="button"
              >
                <Text style={[styles.modalButtonText, { color: '#fff' }]}>Enable</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  userCard: {
    alignItems: 'center',
    padding: 24,
    marginBottom: 16,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  avatarText: {
    color: '#ffffff',
    fontSize: 32,
    fontWeight: 'bold',
  },
  userName: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
  },
  organizationBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(37, 99, 235, 0.1)',
    gap: 6,
  },
  organizationText: {
    fontSize: 14,
    fontWeight: '600',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 8,
    marginLeft: 16,
    letterSpacing: 0.5,
  },
  sectionContent: {
    borderRadius: 12,
    marginHorizontal: 16,
    borderWidth: 1,
    overflow: 'hidden',
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  settingLeft: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    marginBottom: 2,
  },
  settingValue: {
    fontSize: 14,
  },
  settingRight: {
    marginLeft: 12,
  },
  logoutButton: {
    marginHorizontal: 16,
    marginTop: 8,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  logoutButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  footer: {
    padding: 24,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 12,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 340,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    // Web-compatible boxShadow
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 14,
    marginBottom: 20,
    textAlign: 'center',
    lineHeight: 20,
  },
  modalInput: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  modalButtonCancel: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  modalButtonSubmit: {
    // backgroundColor set dynamically
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
